import { useEffect, useState } from 'react';
import { ImageOff, Loader2 } from 'lucide-react';
import api from '../services/api';

/**
 * Ein Bild, das eine Anmeldung verlangt.
 *
 * ## Warum es das braucht
 *
 * Seit Phase 0.5 liefert der Server keine Datei mehr statisch aus. Vorher
 * stand in `index.ts` eine Zeile, die jede hochgeladene Datei an jeden
 * herausgab, der die URL kannte — ohne Anmeldung, ohne Mandantenprüfung.
 * Ein Kunde, der ein PDF mit verlinkten Bildern weiterreichte, gab damit
 * dauerhaften Zugang weiter.
 *
 * Die Kehrseite: `/api/medien/...` verlangt jetzt den Bearer-Token, und
 * ein blankes `<img src="…">` schickt keinen mit. Es bekäme 401 und
 * bliebe leer. Deshalb wird das Bild geholt wie jede andere Antwort auch
 * und als Objekt-URL angezeigt.
 *
 * ## Warum kein Token in der URL
 *
 * Der bequemere Weg wäre `?token=…`. Der landet aber in Server- und
 * Proxyprotokollen, im Verlauf des Browsers und im Referrer — also
 * genau dort, wo der weitergebbare Link wieder entstünde, den diese
 * Umstellung abgeschafft hat.
 *
 * ## Was das kostet
 *
 * Einen Abruf statt eines Bildelements. Der Server setzt
 * `Cache-Control: private, immutable` — der Inhalt hängt am Hash und
 * ändert sich nie —, der zweite Abruf desselben Bildes kommt also aus
 * dem Zwischenspeicher des Browsers.
 */

interface Props {
  /** Pfad wie `/api/medien/42/vorschau`. Leer oder `null` zeigt den Platzhalter. */
  quelle: string | null | undefined;
  alt: string;
  className?: string;
  /** Wird statt des Platzhalters gezeigt, wenn es kein Bild gibt. */
  ersatz?: React.ReactNode;
}

export function MedienBild({ quelle, alt, className, ersatz }: Props) {
  const [objektUrl, setObjektUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    if (!quelle) {
      setObjektUrl(null);
      return;
    }

    let abgebrochen = false;
    let erzeugte: string | null = null;

    setLaedt(true);
    setFehler(false);

    api
      .get(quelle.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((antwort) => {
        if (abgebrochen) return;
        erzeugte = URL.createObjectURL(antwort.data as Blob);
        setObjektUrl(erzeugte);
      })
      .catch(() => {
        if (!abgebrochen) setFehler(true);
      })
      .finally(() => {
        if (!abgebrochen) setLaedt(false);
      });

    return () => {
      abgebrochen = true;
      // Ohne das Freigeben hält der Browser jedes je angezeigte Bild im
      // Speicher, bis die Seite neu geladen wird. Bei einer Begehung mit
      // zweihundert Fotos ist das kein Randfall.
      if (erzeugte) URL.revokeObjectURL(erzeugte);
    };
  }, [quelle]);

  if (!quelle || fehler) {
    return (
      <>
        {ersatz ?? (
          <div
            className={`flex items-center justify-center bg-secondary-100 text-secondary-400 ${className ?? ''}`}
            title={fehler ? 'Das Bild konnte nicht geladen werden.' : 'Kein Bild'}
          >
            <ImageOff size={20} />
          </div>
        )}
      </>
    );
  }

  if (!objektUrl) {
    return (
      <div
        className={`flex items-center justify-center bg-secondary-50 text-secondary-400 ${className ?? ''}`}
        aria-busy={laedt}
      >
        <Loader2 size={18} className="animate-spin" />
      </div>
    );
  }

  return <img src={objektUrl} alt={alt} className={className} />;
}

/**
 * Dasselbe als Datenquelle, wenn kein `<img>` gebraucht wird.
 *
 * Für Konva (`useImage`) und für den Foto-Annotator, die eine URL
 * brauchen und selbst zeichnen.
 */
export function useMedienUrl(quelle: string | null | undefined): {
  url: string | null;
  laedt: boolean;
  fehler: boolean;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);
  const [laedt, setLaedt] = useState(false);

  useEffect(() => {
    if (!quelle) {
      setUrl(null);
      return;
    }

    let abgebrochen = false;
    let erzeugte: string | null = null;

    setLaedt(true);
    setFehler(false);

    api
      .get(quelle.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((antwort) => {
        if (abgebrochen) return;
        erzeugte = URL.createObjectURL(antwort.data as Blob);
        setUrl(erzeugte);
      })
      .catch(() => {
        if (!abgebrochen) setFehler(true);
      })
      .finally(() => {
        if (!abgebrochen) setLaedt(false);
      });

    return () => {
      abgebrochen = true;
      if (erzeugte) URL.revokeObjectURL(erzeugte);
    };
  }, [quelle]);

  return { url, laedt, fehler };
}

export default MedienBild;
